/**
 * Nested slide-in drawer used by the conversation action panels
 * (Transfer / Close / Collaborate / Relations). Renders above the main
 * detail drawer. All corners ≤3px.
 */
import type { JSX, ReactNode } from 'react'
import { X } from 'lucide-react'
import { R } from './primitives.js'
import {
  DrawerInfoButton,
  DrawerInfoPanel,
  useDrawerInfo,
  type DrawerInfoContent,
} from '../../ui/DrawerInfo.js'

export interface ActionDrawerProps {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string | undefined
  icon?: ReactNode
  children: ReactNode
  footer?: ReactNode
  width?: number
  /** When false the body gets no padding/scroll — the child manages its own. */
  padded?: boolean
  /** What this panel does, behind an ⓘ button in the header. Omit and no
   *  button renders — see DrawerInfo.tsx. */
  info?: DrawerInfoContent
}

export default function ActionDrawer({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  footer,
  width = 560,
  padded = true,
  info,
}: ActionDrawerProps): JSX.Element | null {
  // Before the early return — hooks can't run conditionally.
  const drawerInfo = useDrawerInfo(open)

  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div
        className="animate-slide-in-right fixed right-0 top-0 z-[70] flex h-full flex-col bg-white shadow-2xl"
        style={{ width }}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-3">
            {icon && (
              <div className={`flex h-9 w-9 items-center justify-center ${R} bg-[#F5F7FF] text-[#3366FF]`}>
                {icon}
              </div>
            )}
            <div>
              <h2 className="text-sm font-bold text-[#0f172a]">{title}</h2>
              {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {info && <DrawerInfoButton shown={drawerInfo.shown} onToggle={drawerInfo.toggle} />}
            <button
              onClick={onClose}
              aria-label="Close"
              className={`flex h-8 w-8 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {info && (
          <DrawerInfoPanel info={info} shown={drawerInfo.shown} onDismiss={drawerInfo.toggle} />
        )}

        <div className={padded ? 'flex-1 overflow-y-auto px-5 py-5' : 'flex min-h-0 flex-1 flex-col'}>
          {children}
        </div>

        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </>
  )
}
